import { useEffect, useRef, useState } from 'react'

import { useSession } from '../../../../../contexts/SessionContext'
import { useDirectDebitStatus } from '../../../../../contexts/session/DirectDebitContext'
import { SessionStatus } from '../../../../../constants/SessionConstants'
import {
  MIN_AUTHORIZATION_AMOUNT_NGN,
  MAX_AUTHORIZATION_AMOUNT_NGN,
} from '../../../../../constants/DirectDebitConstants'
import { formatAuthorizationAmount } from '../../../../../utils/directDebitFormatting'

import StepHeader from '../../../../elements/direct-debit/StepHeader'
import DataTable from '../../../../elements/direct-debit/DataTable'
import Callout, { calloutEmphasisClassName } from '../../../../elements/direct-debit/Callout'
import Button from '../../../../elements/direct-debit/Button'
import Toast from '../../../../elements/direct-debit/Toast'
import styles from './OneTimeTransferScreen.module.css'

const DIRECT_DEBIT_PAYMENT_METHOD = 'BANK_ACCOUNT_DIRECT_DEBIT'
const POLL_INTERVAL_MS = 20000

// Spec screen 05 - ported from the legacy TransferConfirmationScreen.js's data shape.
// Polling starts as soon as this screen mounts, not gated behind "I have made the
// Transfer" - the backend detects the bank transfer independently of that click, so
// checking should already be happening under the hood while the user reads the
// instructions. The button just gives explicit visual feedback (spinner) and forces an
// immediate check on top of the running interval, rather than being what starts polling.
// Each poll's response is checked directly here (same SUCCESS/CONFIRMED/FAILED/CANCELLED
// handling the old CheckingTransferScreen used), driving showMandateCreated/showFailed
// itself once the backend confirms.
const OneTimeTransferScreen = () => {
  const { loadSessionData } = useSession()
  const { transferDetails, accountNumber, selectedBank, openSheet, sessionKey, showMandateCreated, showFailed } =
    useDirectDebitStatus()
  const [toast, setToast] = useState({ show: false, message: '' })
  const [checkingTransfer, setCheckingTransfer] = useState(false)
  const pollIntervalRef = useRef(null)
  const resolvedRef = useRef(false)

  const checkTransferStatus = async () => {
    if (resolvedRef.current) return
    try {
      const response = await loadSessionData(sessionKey)
      const status = response?.data?.status

      if (status === SessionStatus.SUCCESS || status === SessionStatus.CONFIRMED) {
        resolvedRef.current = true
        clearInterval(pollIntervalRef.current)
        showMandateCreated()
      } else if (status === SessionStatus.FAILED || status === SessionStatus.CANCELLED) {
        resolvedRef.current = true
        clearInterval(pollIntervalRef.current)
        showFailed(response?.data?.message || response?.statusMessage || 'Your transfer could not be confirmed.')
      }
    } catch {
      // transient error - the next interval tick retries
    }
  }

  useEffect(() => {
    checkTransferStatus()
    pollIntervalRef.current = setInterval(checkTransferStatus, POLL_INTERVAL_MS)
    return () => clearInterval(pollIntervalRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTransferMade = () => {
    setCheckingTransfer(true)
    checkTransferStatus()
  }

  const directDebitDetail = transferDetails?.paymentMethodDetails?.find(
    (detail) => detail.paymentMethod === DIRECT_DEBIT_PAYMENT_METHOD,
  )
  const activationData = directDebitDetail?.data || {}
  const authorizationAmount = transferDetails?.metadata?.authorizationAmount
  const { displayAmount, displayAmountLabel } = formatAuthorizationAmount(
    authorizationAmount,
    MIN_AUTHORIZATION_AMOUNT_NGN,
    MAX_AUTHORIZATION_AMOUNT_NGN,
  )

  const rows = [
    { icon: 'table-currency', label: displayAmountLabel, value: displayAmount, labelColor: 'secondary' },
    {
      icon: 'table-user-focus',
      label: 'Account number',
      value: activationData.activationAccountNumber || '',
      copyable: true,
      labelColor: 'secondary',
    },
    { icon: 'table-user', label: 'Account name', value: activationData.activationAccountName || '', labelColor: 'secondary' },
    { icon: 'table-bank', label: 'Bank', value: activationData.activationBankName || '', labelColor: 'secondary' },
  ]

  return (
    <>
      <StepHeader
        title='Transfer to authorize'
        step={2}
        description="Send the exact amount below from your account. It's the last step."
      />
      <div className={styles.body}>
        <div className={styles.content}>
          <DataTable rows={rows} onCopy={() => setToast({ show: true, message: 'Account number copied' })} />
          <Callout variant='info'>
            {accountNumber && selectedBank?.bankName ? (
              <>
                Send from <span className={calloutEmphasisClassName}>{selectedBank.bankName} {accountNumber}</span> only. Transfers from other accounts won't switch it on.
              </>
            ) : (
              "Send from the account you are trying to authorize only. Transfers from other accounts won't switch it on."
            )}
          </Callout>
          <Button variant='link' className={styles.whyLink} onClick={() => openSheet('why-fifty')}>
            Why do I need to make this transfer?
          </Button>
        </div>
        <div className={styles.bottom}>
          <Button onClick={handleTransferMade} loading={checkingTransfer}>
            I have made the Transfer
          </Button>
        </div>
      </div>
      <Toast show={toast.show} message={toast.message} onDismiss={() => setToast({ show: false, message: '' })} />
    </>
  )
}

export default OneTimeTransferScreen
